import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Radio, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import CustomSelect, { MenuData } from "../../../components/input/CustomSelect";
import CustomDatePicker from "../../../components/input/CustomDatePicker";
import { common, financedEmissions } from "../../../i18n";
import { AssetClass } from "../../../models/dto/cat15/cat15.types";

export interface BorrowerOption {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  borrowers: BorrowerOption[];
  /** pre-select + lock the borrower (borrower-detail "Attach New Loan") */
  lockedBorrowerId?: string;
  onSubmit?: (values: AddLoanForm) => void;
}

export interface AddLoanForm {
  borrowerId: string;
  assetClass: AssetClass;
  loanRef: string;
  principal: string;
  /** YYYY-MM-DD */
  origination: string;
  /** YYYY-MM-DD, optional */
  maturity: string;
  /** initial-snapshot year */
  year: string;
  outstanding: string;
}

const YEAR_SPAN = 7;

// Mask a loan reference to the hint shape "LN-2025-0001": 2 letters +
// 4-digit year + 4-digit sequence, auto-uppercased and dash-separated.
const formatLoanRef = (raw: string): string => {
  let letters = "";
  let digits = "";
  for (const c of raw.toUpperCase()) {
    if (letters.length < 2 && c >= "A" && c <= "Z") letters += c;
    else if (digits.length < 8 && c >= "0" && c <= "9") digits += c;
  }
  const parts: string[] = [];
  if (letters) parts.push(letters);
  if (digits) parts.push(digits.slice(0, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.join("-");
};

// Asset-class radio cards — Figma node 6119:7967.
const assetCards: {
  value: AssetClass;
  titleKey: string;
  descKey: string;
}[] = [
  {
    value: "business",
    titleKey: "Asset-Business-Loans-Full",
    descKey: "Asset-Business-Desc",
  },
  {
    value: "listed",
    titleKey: "Asset-Listed-Equity",
    descKey: "Asset-Listed-Desc",
  },
  {
    value: "project",
    titleKey: "Asset-Project-Finance",
    descKey: "Asset-Project-Desc",
  },
];

const AddLoanModal = ({
  open,
  onClose,
  borrowers,
  lockedBorrowerId,
  onSubmit,
}: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const { control, handleSubmit, watch, setValue, reset } = useForm<AddLoanForm>(
    {
      defaultValues: {
        borrowerId: lockedBorrowerId ?? "",
        assetClass: "business",
        loanRef: "",
        principal: "",
        origination: "",
        maturity: "",
        year: "",
        outstanding: "",
      },
    },
  );
  const [origination, setOrigination] = useState<Dayjs | null>(null);
  const [maturity, setMaturity] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (open) {
      reset({
        borrowerId: lockedBorrowerId ?? "",
        assetClass: "business",
        loanRef: "",
        principal: "",
        origination: "",
        maturity: "",
        year: "",
        outstanding: "",
      });
      setOrigination(null);
      setMaturity(null);
    }
  }, [open, lockedBorrowerId, reset]);

  const borrowerOptions: MenuData[] = borrowers.map((b) => ({
    value: b.id,
    nameEN: b.name,
    nameTH: b.name,
  }));

  const yearOptions: MenuData[] = useMemo(() => {
    const thisYear = new Date().getFullYear();
    return Array.from({ length: YEAR_SPAN }, (_, i) => thisYear - i).map((y) => ({
      value: String(y),
      nameEN: String(y),
      nameTH: String(y),
    }));
  }, []);

  const borrowerId = watch("borrowerId");
  const assetClass = watch("assetClass");
  const year = watch("year");

  const submit = (values: AddLoanForm) => {
    onSubmit?.({
      ...values,
      origination: origination ? origination.format("YYYY-MM-DD") : "",
      maturity: maturity ? maturity.format("YYYY-MM-DD") : "",
    });
    onClose();
  };

  const disabled = !borrowerId || !origination || !year;

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="620px"
      header={<Typography variant="h1">+ {t("Attach-New-Loan")}</Typography>}
      body={
        <Box display="flex" flexDirection="column" gap="20px">
          <CustomSelect
            required
            label={t("Col-Borrower")}
            value={borrowerId}
            placeholder={t("Please-Select-Borrower")}
            listMenuItem={borrowerOptions}
            disabled={!!lockedBorrowerId}
            handleValeOnChange={(e) => setValue("borrowerId", e.target.value)}
          />

          {/* Asset Class radio cards */}
          <Box>
            <Typography
              fontSize="16px"
              fontWeight={500}
              color="var(--dark)"
              mb="8px"
            >
              {t("Col-Asset-Class")}{" "}
              <Box component="span" color="var(--red)">
                *
              </Box>
            </Typography>
            <Box display="flex" flexDirection="column" gap="8px">
              {assetCards.map((card) => {
                const selected = assetClass === card.value;
                return (
                  <Box
                    key={card.value}
                    onClick={() => setValue("assetClass", card.value)}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      p: "12px",
                      borderRadius: "8px",
                      border: selected
                        ? "1px solid var(--green)"
                        : "1px solid var(--gray5)",
                      bgcolor: selected ? "var(--green3)" : "var(--white)",
                    }}
                  >
                    <Radio
                      checked={selected}
                      size="small"
                      sx={{ p: 0, mt: "2px", color: "var(--gray1)", "&.Mui-checked": { color: "var(--green)" } }}
                    />
                    <Box>
                      <Typography
                        fontSize="16px"
                        fontWeight={500}
                        color="var(--dark)"
                      >
                        {t(card.titleKey)}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        lineHeight="16px"
                        color="var(--gray1)"
                        whiteSpace="pre-line"
                      >
                        {t(card.descKey)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Grid container spacing="20px">
            <Grid xs={12} sm={6}>
              <InputReactHookForm
                required
                name="loanRef"
                control={control}
                label={t("Loan-Reference")}
                placeholder="LN-2025-0001"
                maxLength={12}
                formatValue={formatLoanRef}
                rules={{ required: t("Loan-Reference") }}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <InputReactHookForm
                required
                name="principal"
                control={control}
                label={t("Col-Principal")}
                placeholder="0.00"
                rules={{ required: t("Col-Principal") }}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <CustomDatePicker
                required
                label={t("Origination-Date")}
                info={t("Origination-Date")}
                views={["year", "month", "day"]}
                format="DD/MM/YYYY"
                value={origination}
                defaultDate={null}
                handleOnChange={(d: Dayjs | null) =>
                  setOrigination(d ? dayjs(d) : null)
                }
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <CustomDatePicker
                label={t("Maturity-Date")}
                info={t("Maturity-Date")}
                views={["year", "month", "day"]}
                format="DD/MM/YYYY"
                value={maturity}
                defaultDate={null}
                handleOnChange={(d: Dayjs | null) =>
                  setMaturity(d ? dayjs(d) : null)
                }
              />
            </Grid>
          </Grid>

          <Box>
            <Typography variant="body2" color="var(--gray1)" mb="8px">
              {t("Initial-Snapshot")}
            </Typography>
            <Grid container spacing="20px">
              <Grid xs={12} sm={6}>
                <CustomSelect
                  required
                  label={t("Col-Year")}
                  value={year}
                  placeholder={t("Select-Year")}
                  listMenuItem={yearOptions}
                  handleValeOnChange={(e) => setValue("year", e.target.value)}
                />
              </Grid>
              <Grid xs={12} sm={6}>
                <InputReactHookForm
                  required
                  name="outstanding"
                  control={control}
                  label={t("Outstanding-Amount-THB")}
                  placeholder="0.00"
                  rules={{ required: t("Outstanding-Amount-THB") }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" gap="12px">
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={onClose}
            >
              {t("Cancel")}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              disabled={disabled}
              onClick={handleSubmit(submit)}
            >
              {t("Attach-Loan-Btn")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default AddLoanModal;
